import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft, Phone, X, Delete, Users } from 'lucide-react';
import { SpeedDials } from '@/lib/api';
import { useSoftphone } from '@/store/softphone';

/**
 * Çağrı aktarma popover'ı. Topbar Aktar butonunun altına anchor olur.
 *
 * Global state'ten yönetilir — softphone store'undaki `transferSheetOpen`
 * + `transferSheetAnchor` (viewport top/right koords). Topbar Aktar butonu
 * tıklanırken kendi rect'ini geçirir; popover oraya yapışır.
 *
 * Yapı:
 *  - Fixed positioning, anchor.bottom + 8px altta, sağ-hizalı
 *  - Backdrop yok (overlay olmadan, click-outside ile kapanır)
 *  - 320px sabit genişlik, max-h 480px scroll
 *  - Çağrı bitince otomatik kapanır
 */
export function TransferSheet() {
  const transfer = useSoftphone((s) => s.transfer);
  const close = useSoftphone((s) => s.closeTransferSheet);
  const open = useSoftphone((s) => s.transferSheetOpen);
  const anchor = useSoftphone((s) => s.transferSheetAnchor);
  const callState = useSoftphone((s) => s.state);

  const [target, setTarget] = useState('');
  const { data: speedDials } = useQuery({
    queryKey: ['speed-dials'],
    queryFn: SpeedDials.list,
    enabled: open,
  });

  // Click-outside listener — popover dışına tıklarsa kapanır
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-transfer-popover]') && !el.closest('[data-transfer-trigger]')) {
        close();
      }
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    // Defer to next tick so the opening click doesn't immediately close it
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onEsc);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, close]);

  // Çağrı biterse popover'ı kapat
  useEffect(() => {
    if (open && callState !== 'in_call') close();
  }, [open, callState, close]);

  if (!open) return null;

  const trimmed = target.trim();

  const handleConfirm = (dest: string) => {
    transfer(dest);
    setTarget('');
    close();
  };
  const padKey = (k: string) => setTarget((n) => n + k);
  const backspace = () => setTarget((n) => n.slice(0, -1));

  // Anchor varsa popover, yoksa viewport ortası fallback
  const positionStyle: React.CSSProperties = anchor
    ? { position: 'fixed', top: anchor.top, right: anchor.right, zIndex: 50 }
    : { position: 'fixed', top: 80, right: 20, zIndex: 50 };

  return (
    <div
      data-transfer-popover
      style={positionStyle}
      className="w-[320px] bg-surface rounded-2xl shadow-[0_18px_50px_-12px_rgba(15,23,42,0.25)] ring-1 ring-black/5 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100">
        <h3 className="text-[13px] font-semibold text-slate-900 tracking-tight flex items-center gap-1.5">
          <ArrowRightLeft size={13} className="text-blue-600" /> Çağrıyı Aktar
        </h3>
        <button onClick={close} className="p-1 text-slate-400 hover:text-slate-700 rounded">
          <X size={14} />
        </button>
      </div>

      <div className="p-3">
        {/* Input + backspace */}
        <div className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100/70 rounded-lg ring-1 ring-transparent focus-within:bg-surface focus-within:ring-blue-300 transition-all">
          <Phone size={12} className="text-slate-400 flex-shrink-0" />
          <input
            autoFocus
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Numara veya extension"
            className="flex-1 min-w-0 bg-transparent text-[13px] font-mono text-slate-800 placeholder:font-sans placeholder:text-slate-400 placeholder:text-[12px] focus:outline-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && trimmed) handleConfirm(trimmed);
            }}
          />
          <button
            onClick={backspace}
            disabled={!target}
            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-25 rounded transition"
            title="Sil"
          >
            <Delete size={12} />
          </button>
        </div>

        {/* Quick dial list */}
        {speedDials && speedDials.length > 0 && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between px-1 mb-1">
              <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <Users size={10} className="text-slate-400" />
                Hızlı Dahili
              </span>
              <span className="text-[10px] text-slate-400">{speedDials.length}</span>
            </div>
            <ul className="bg-slate-50/60 rounded-lg ring-1 ring-slate-200/70 divide-y divide-slate-100 max-h-[120px] overflow-y-auto">
              {speedDials.map((sd) => (
                <li key={sd.id}>
                  <button
                    onClick={() => handleConfirm(sd.number)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-surface active:bg-slate-100 transition text-left"
                  >
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Phone size={10} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium text-slate-800 truncate">{sd.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{sd.number}</div>
                    </div>
                    <ArrowRightLeft size={10} className="text-slate-300 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mini keypad */}
        <div className="grid grid-cols-3 gap-1 mt-2.5">
          {['1','2','3','4','5','6','7','8','9','*','0','#'].map((k) => (
            <button
              key={k}
              onClick={() => padKey(k)}
              className="h-8 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-800 text-[13px] font-medium active:bg-slate-200 active:scale-[0.97] transition-all duration-100"
            >
              {k}
            </button>
          ))}
        </div>

        {/* Action button */}
        <button
          onClick={() => trimmed && handleConfirm(trimmed)}
          disabled={!trimmed}
          className="w-full mt-2.5 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-[12.5px] font-semibold transition flex items-center justify-center gap-1.5"
        >
          <ArrowRightLeft size={12} />
          Aktar
        </button>
      </div>
    </div>
  );
}
